import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PageHeader } from '@/components/admin/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCw, Play, RotateCcw, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

type OutboxStatus = 'pending' | 'sent' | 'failed' | 'permanently_failed';

interface OutboxRow {
  id: string;
  created_at: string;
  status: string;
  template: string;
  to_email: string;
  subject: string;
  retry_count: number;
  payload: Record<string, unknown>;
  sent_at: string | null;
  dedupe_key: string | null;
  error_message: string | null;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alla' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'permanently_failed', label: 'Perm. Failed' },
];

function statusColor(status: string): string {
  switch (status) {
    case 'sent': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'pending': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'permanently_failed': return 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-200';
    default: return 'bg-muted text-muted-foreground';
  }
}

export default function AdminEmailOutboxPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<OutboxRow | null>(null);

  // Track page view
  useEffect(() => {
    track('admin_outbox_viewed', {} as Record<string, never>);
  }, []);

  // Fetch outbox rows
  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['admin-email-outbox', statusFilter, templateFilter, search],
    queryFn: async () => {
      let query = supabase
        .from('email_outbox')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (templateFilter !== 'all') {
        query = query.eq('template', templateFilter);
      }
      if (search.trim()) {
        query = query.or(`to_email.ilike.%${search.trim()}%,subject.ilike.%${search.trim()}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as OutboxRow[];
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Distinct templates for filter
  const { data: templates = [] } = useQuery({
    queryKey: ['admin-email-outbox-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_outbox')
        .select('template')
        .limit(500);
      if (error) throw error;
      const unique = [...new Set((data ?? []).map((r: { template: string }) => r.template))].sort();
      return unique;
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Retry mutation (reset to pending only)
  const retryMutation = useMutation({
    mutationFn: async (id: string) => {
      track('admin_outbox_retry_clicked', { outbox_id: id } as any);
      const { error } = await supabase
        .from('email_outbox')
        .update({ status: 'pending', retry_count: 0, error_message: null } as any)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Status återställd till pending');
      queryClient.invalidateQueries({ queryKey: ['admin-email-outbox'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Send single email mutation (reset + process immediately)
  const sendSingleMutation = useMutation({
    mutationFn: async (id: string) => {
      track('admin_outbox_send_single_clicked', { outbox_id: id } as any);
      const { data, error } = await supabase.functions.invoke('admin-process-email-outbox', {
        method: 'POST',
        body: { email_id: id },
      });
      if (error) throw error;
      return data as { processed?: number; success?: number; failed?: number; error?: string };
    },
    onSuccess: (data) => {
      if (data?.error) {
        toast.error(`Fel: ${data.error}`);
      } else if (data?.success && data.success > 0) {
        toast.success('E-post skickad');
      } else {
        toast.error('Kunde inte skicka e-post');
      }
      queryClient.invalidateQueries({ queryKey: ['admin-email-outbox'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Process mutation
  const processMutation = useMutation({
    mutationFn: async () => {
      track('admin_outbox_process_clicked', { status_filter: statusFilter } as any);
      const { data, error } = await supabase.functions.invoke('admin-process-email-outbox', {
        method: 'POST',
      });
      if (error) throw error;
      return data as { processed?: number; success?: number; failed?: number; error?: string };
    },
    onSuccess: (data) => {
      if (data?.error) {
        toast.error(`Fel: ${data.error}`);
      } else {
        toast.success(`Klart: ${data?.processed ?? 0} processade, ${data?.success ?? 0} skickade, ${data?.failed ?? 0} misslyckade`);
      }
      queryClient.invalidateQueries({ queryKey: ['admin-email-outbox'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const formatDate = (d: string) => {
    try { return format(new Date(d), 'dd MMM HH:mm', { locale: sv }); }
    catch { return d; }
  };

  return (
    <div className="min-h-0 flex flex-col">
      <PageHeader
        title="E-postkö"
        description="Övervaka och hantera utgående e-post"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Uppdatera
            </Button>
            <Button
              size="sm"
              onClick={() => processMutation.mutate()}
              disabled={processMutation.isPending}
            >
              <Play className="h-4 w-4 mr-1.5" />
              Kör jobb nu
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="px-4 lg:px-8 pb-4 flex flex-wrap gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Mall" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla mallar</SelectItem>
            {templates.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          placeholder="Sök e-post / ämne..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-[220px]"
        />
      </div>

      {/* Desktop table */}
      <div className="hidden md:block px-4 lg:px-8 pb-8 overflow-auto">
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px]">Skapad</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[150px]">Mall</TableHead>
                <TableHead>Mottagare</TableHead>
                <TableHead>Ämne</TableHead>
                <TableHead className="w-[60px]">Försök</TableHead>
                <TableHead className="w-[100px]">Åtgärd</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Laddar...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Inga rader att visa
                  </TableCell>
                </TableRow>
              ) : rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {formatDate(row.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={statusColor(row.status)}>
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono">{row.template}</TableCell>
                  <TableCell className="text-sm truncate max-w-[180px]">{row.to_email}</TableCell>
                  <TableCell className="text-sm truncate max-w-[200px]">{row.subject}</TableCell>
                  <TableCell className="text-center text-xs">{row.retry_count}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedRow(row)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {row.status !== 'sent' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => sendSingleMutation.mutate(row.id)}
                          disabled={sendSingleMutation.isPending}
                          title="Skicka nu"
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {(row.status === 'failed' || row.status === 'permanently_failed') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => retryMutation.mutate(row.id)}
                          disabled={retryMutation.isPending}
                          title="Återställ till pending"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden px-4 pb-8 space-y-3">
        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">Laddar...</p>
        ) : rows.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Inga rader att visa</p>
        ) : rows.map(row => (
          <div key={row.id} className="border rounded-lg p-3 space-y-2 bg-card">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className={statusColor(row.status)}>
                {row.status}
              </Badge>
              <span className="text-xs font-mono text-muted-foreground">{formatDate(row.created_at)}</span>
            </div>
            <div>
              <p className="text-sm font-medium truncate">{row.subject}</p>
              <p className="text-xs text-muted-foreground truncate">{row.to_email}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-mono">{row.template} · {row.retry_count} försök</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedRow(row)}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                {row.status !== 'sent' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => sendSingleMutation.mutate(row.id)}
                    disabled={sendSingleMutation.isPending}
                    title="Skicka nu"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                )}
                {(row.status === 'failed' || row.status === 'permanently_failed') && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => retryMutation.mutate(row.id)}
                    disabled={retryMutation.isPending}
                    title="Återställ till pending"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            {row.error_message && (
              <p className="text-xs text-destructive truncate">{row.error_message}</p>
            )}
          </div>
        ))}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selectedRow} onOpenChange={open => !open && setSelectedRow(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>E-postdetalj</DialogTitle>
          </DialogHeader>
          {selectedRow && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-[100px_1fr] gap-y-2 gap-x-3">
                <span className="text-muted-foreground">ID</span>
                <span className="font-mono text-xs break-all">{selectedRow.id}</span>
                <span className="text-muted-foreground">Status</span>
                <Badge variant="secondary" className={statusColor(selectedRow.status)}>{selectedRow.status}</Badge>
                <span className="text-muted-foreground">Mall</span>
                <span className="font-mono">{selectedRow.template}</span>
                <span className="text-muted-foreground">Mottagare</span>
                <span>{selectedRow.to_email}</span>
                <span className="text-muted-foreground">Ämne</span>
                <span>{selectedRow.subject}</span>
                <span className="text-muted-foreground">Skapad</span>
                <span>{selectedRow.created_at}</span>
                <span className="text-muted-foreground">Skickad</span>
                <span>{selectedRow.sent_at || '–'}</span>
                <span className="text-muted-foreground">Försök</span>
                <span>{selectedRow.retry_count}</span>
                {selectedRow.error_message && (
                  <>
                    <span className="text-muted-foreground">Senaste fel</span>
                    <span className="text-destructive text-xs break-all">{selectedRow.error_message}</span>
                  </>
                )}
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Payload</p>
                <pre className="bg-muted rounded p-3 text-xs overflow-auto max-h-[300px] whitespace-pre-wrap break-all">
                  {JSON.stringify(selectedRow.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
